// Cross-origin auth handoff between workspace shell (my.symbols.app) and
// public preview surfaces (*.preview.symbols.app). The two origins do NOT
// share localStorage, so when a logged-in user lands on a preview URL for
// a PRIVATE project we have to:
//
//   1. Redirect the preview to my.symbols.app/signin?redirect=<previewUrl>
//   2. Workspace signs the user in normally (cookies + localStorage on
//      my.symbols.app)
//   3. Workspace mints a one-shot fragment payload and redirects back:
//      <previewUrl>#__symbols_auth=<base64(json{access,refresh,expiresAt})>
//   4. Preview reads the fragment BEFORE the SDK constructs its
//      TokenManager (which hydrates from localStorage in its constructor),
//      writes the tokens into preview-origin localStorage, then scrubs
//      the fragment from history so the credential never persists in
//      browser history or gets bookmarked.
//
// Fragment (not query) is used because (a) fragments aren't sent to
// the server (no access-log leak) and (b) they aren't part of any
// referer header. The payload itself is short-lived — it carries the
// SDK access token (already in the user's localStorage on
// my.symbols.app), not a freshly minted credential.
//
// Redirect targets are restricted to known preview hosts so a malicious
// page can't trick workspace into handing tokens to an attacker origin.

const PREVIEW_HOST_RE = /(^|\.)preview\.(symbols\.app|localhost)$/i

const HANDOFF_FRAGMENT_KEY = '__symbols_auth'

const TOKEN_STORAGE_KEYS = {
  accessToken: 'symbols_access_token',
  refreshToken: 'symbols_refresh_token',
  expiresAt: 'symbols_expires_at',
  expiresIn: 'symbols_expires_in'
}

// True when `url` points at a host the preview surface trusts. We accept
// the same origin (so the workspace shell can sign-in flow back to
// itself) AND any *.preview.* host that this codebase serves.
export const isTrustedHandoffTarget = (url) => {
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://localhost')
    if (typeof window !== 'undefined' && u.origin === window.location.origin) return true
    return PREVIEW_HOST_RE.test(u.hostname)
  } catch {
    return false
  }
}

// Build the workspace-side redirect URL: appends `?redirect=<currentUrl>`
// onto `<workspaceOrigin>/signin`. Caller passes the workspace origin
// (resolved via `workspaceOrigin()` from canvasUrl.js).
export const buildLoginRedirectUrl = (workspaceOrigin, returnTo) => {
  const url = new URL('/signin', workspaceOrigin)
  url.searchParams.set('redirect', returnTo)
  return url.toString()
}

// Encode tokens into a fragment payload. Workspace calls this after
// successful login when `?redirect=` points at a trusted preview host.
export const encodeHandoffPayload = ({ accessToken, refreshToken, expiresAt, expiresIn }) => {
  const payload = { a: accessToken, r: refreshToken || '', x: expiresAt || 0, i: expiresIn || 0 }
  // base64url so URL-safe without further escaping.
  const json = JSON.stringify(payload)
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    return window.btoa(unescape(encodeURIComponent(json)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  }
  return Buffer.from(json, 'utf8').toString('base64url')
}

const decodeHandoffPayload = (token) => {
  try {
    const padded = token.replace(/-/g, '+').replace(/_/g, '/')
    const json = typeof window !== 'undefined'
      ? decodeURIComponent(escape(window.atob(padded)))
      : Buffer.from(padded, 'base64').toString('utf8')
    return JSON.parse(json)
  } catch {
    return null
  }
}

// Append the handoff payload to a URL's fragment. Preserves any existing
// fragment (rare for redirect targets, but defensive).
export const appendHandoffFragment = (url, encodedPayload) => {
  const u = new URL(url)
  // Use a key=value fragment shape so the preview can scrub just our key
  // without nuking unrelated SPA state someone might have placed there.
  const existing = u.hash.replace(/^#/, '')
  const parts = existing ? existing.split('&').filter(p => !p.startsWith(`${HANDOFF_FRAGMENT_KEY}=`)) : []
  parts.push(`${HANDOFF_FRAGMENT_KEY}=${encodedPayload}`)
  u.hash = parts.join('&')
  return u.toString()
}

// Preview-side: pull the handoff fragment, persist tokens into
// preview-origin localStorage so the SDK's TokenManager picks them up
// during construction, then scrub the fragment from the URL.
//
// MUST be called BEFORE `getSDK()` is first invoked — the TokenManager
// loads from localStorage in its constructor.
export const consumeHandoffFromUrl = () => {
  if (typeof window === 'undefined') return false
  const hash = window.location.hash || ''
  if (!hash.includes(`${HANDOFF_FRAGMENT_KEY}=`)) return false
  const params = new Map(
    hash.replace(/^#/, '').split('&').filter(Boolean).map(p => {
      const idx = p.indexOf('=')
      return idx === -1 ? [p, ''] : [p.slice(0, idx), p.slice(idx + 1)]
    })
  )
  const token = params.get(HANDOFF_FRAGMENT_KEY)
  if (!token) return false
  const decoded = decodeHandoffPayload(token)
  if (!decoded?.a) return false

  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEYS.accessToken, decoded.a)
    if (decoded.r) window.localStorage.setItem(TOKEN_STORAGE_KEYS.refreshToken, decoded.r)
    if (decoded.x) window.localStorage.setItem(TOKEN_STORAGE_KEYS.expiresAt, String(decoded.x))
    if (decoded.i) window.localStorage.setItem(TOKEN_STORAGE_KEYS.expiresIn, String(decoded.i))
  } catch (err) {
    console.warn('[authHandoff] failed to persist tokens:', err?.message || err)
    return false
  }

  // Scrub the fragment so the credential doesn't sit in browser history
  // or the address bar. Keep any other fragment keys intact.
  params.delete(HANDOFF_FRAGMENT_KEY)
  const remaining = Array.from(params.entries())
    .map(([k, v]) => v ? `${k}=${v}` : k).join('&')
  const url = new URL(window.location.href)
  url.hash = remaining ? `#${remaining}` : ''
  try {
    window.history.replaceState(null, '', url.toString())
  } catch {
    // history API unavailable; fragment will linger but tokens are persisted.
  }
  return true
}

export { HANDOFF_FRAGMENT_KEY, TOKEN_STORAGE_KEYS, PREVIEW_HOST_RE }
